import { useEffect, useMemo, useState } from 'react'
import { HelpCircle, X, Search, BookOpen, ExternalLink, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface WikiIndexEntry {
  id: number
  slug: string
  title: string
  category: string
  updated_at: string
}

interface WikiPage extends WikiIndexEntry {
  content: string
}

// Cheap markdown-ish renderer — we only generated pages with #, ##, - * and `code` blocks,
// so a small formatter is enough and avoids pulling in a full markdown dep.
function renderContent(raw: string): React.ReactNode {
  const lines = raw.split('\n')
  const out: React.ReactNode[] = []
  let inCode = false
  let inTable = false
  let codeBuf: string[] = []
  let listBuf: string[] = []
  let tableRows: string[][] = []
  const flushList = () => {
    if (listBuf.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc ml-5 space-y-0.5 text-[11px] text-text-secondary mb-2">
          {listBuf.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inlineFmt(item) }} />
          ))}
        </ul>
      )
      listBuf = []
    }
  }
  const flushTable = () => {
    if (tableRows.length) {
      out.push(
        <div key={`t-${out.length}`} className="overflow-x-auto mb-2">
          <table className="text-[10px] border-collapse">
            <thead>
              <tr>{tableRows[0].map((h, i) => (
                <th key={i} className="border border-border px-1.5 py-0.5 text-left bg-surface-raised/50">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {tableRows.slice(2).map((r, ri) => (
                <tr key={ri}>{r.map((c, ci) => (
                  <td key={ci} className="border border-border px-1.5 py-0.5" dangerouslySetInnerHTML={{ __html: inlineFmt(c) }} />
                ))}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )
      tableRows = []
    }
  }
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (line.startsWith('```')) {
      if (inCode) {
        out.push(
          <pre key={`c-${out.length}`} className="bg-surface-raised/50 border border-border rounded p-2 text-[10px] font-mono overflow-x-auto mb-2">{codeBuf.join('\n')}</pre>
        )
        codeBuf = []
      }
      inCode = !inCode
      continue
    }
    if (inCode) { codeBuf.push(line); continue }

    // Tables — detect pipe-separated row followed by |---| divider
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      if (!inTable) { inTable = true; tableRows = [] }
      tableRows.push(line.trim().slice(1, -1).split('|').map(c => c.trim()))
      continue
    } else if (inTable) {
      flushTable(); inTable = false
    }

    if (line.startsWith('## ')) {
      flushList()
      out.push(<h3 key={`h3-${out.length}`} className="text-[11px] font-semibold text-text-primary mt-3 mb-1">{line.slice(3)}</h3>)
      continue
    }
    if (line.startsWith('### ')) {
      flushList()
      out.push(<h4 key={`h4-${out.length}`} className="text-[10px] font-semibold text-text-primary mt-2 mb-1 uppercase tracking-wider">{line.slice(4)}</h4>)
      continue
    }
    if (line.startsWith('# ')) {
      flushList()
      out.push(<h2 key={`h2-${out.length}`} className="text-[13px] font-bold text-text-primary mt-3 mb-2">{line.slice(2)}</h2>)
      continue
    }
    if (/^\s*[-*]\s+/.test(line)) {
      listBuf.push(line.replace(/^\s*[-*]\s+/, ''))
      continue
    }
    flushList()
    if (line.trim() === '') {
      out.push(<div key={`sp-${out.length}`} className="h-1" />)
    } else {
      out.push(
        <p key={`p-${out.length}`} className="text-[11px] text-text-secondary leading-relaxed mb-1.5"
           dangerouslySetInnerHTML={{ __html: inlineFmt(line) }} />
      )
    }
  }
  flushList()
  flushTable()
  return out
}

function inlineFmt(s: string): string {
  // Escape HTML first
  let out = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  // Inline code
  out = out.replace(/`([^`]+)`/g, '<code class="font-mono text-[10px] bg-surface-raised px-1 rounded text-blue-light">$1</code>')
  // Bold
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-text-primary">$1</strong>')
  // Links [text](url)
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer noopener" class="text-blue-light hover:underline">$1</a>')
  return out
}

export default function HelpPanel() {
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState<WikiIndexEntry[]>([])
  const [currentSlug, setCurrentSlug] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState<WikiPage | null>(null)
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || index.length) return
    api.get('/api/content/wiki').then(r => setIndex(r.data.pages || [])).catch(() => {})
  }, [open, index.length])

  useEffect(() => {
    if (!currentSlug) { setCurrentPage(null); return }
    setLoading(true)
    api.get(`/api/content/wiki/${currentSlug}`)
      .then(r => setCurrentPage(r.data))
      .finally(() => setLoading(false))
  }, [currentSlug])

  const byCategory = useMemo(() => {
    const q = query.trim().toLowerCase()
    const filtered = q ? index.filter(p => p.title.toLowerCase().includes(q) || p.slug.includes(q) || p.category.toLowerCase().includes(q)) : index
    const groups: Record<string, WikiIndexEntry[]> = {}
    filtered.forEach(p => { (groups[p.category] ||= []).push(p) })
    return groups
  }, [index, query])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-[60] w-11 h-11 rounded-full bg-blue text-white shadow-xl hover:bg-blue-dark hover:scale-105 transition-all flex items-center justify-center"
        title="Open knowledge base"
        aria-label="Open help / knowledge base"
      >
        <HelpCircle size={20} />
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex justify-end" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-2xl bg-surface border-l border-border flex flex-col shadow-2xl">
            {/* Header */}
            <div className="shrink-0 bg-surface border-b border-border p-3 flex items-center justify-between z-10">
              <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                <BookOpen size={14} className="text-blue" /> Knowledge Base
                {currentPage && (
                  <>
                    <ArrowRight size={11} className="text-text-muted" />
                    <span className="text-text-secondary font-normal">{currentPage.title}</span>
                  </>
                )}
              </h2>
              <div className="flex items-center gap-1">
                <Link
                  to="/app/wiki"
                  onClick={() => setOpen(false)}
                  className="text-[10px] text-text-muted hover:text-blue-light inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-surface-hover"
                  title="Open full wiki page"
                >
                  Full view <ExternalLink size={10} />
                </Link>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="btn-ghost p-1 rounded"
                  title="Close"
                  aria-label="Close help"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Body: index or article */}
            <div className="flex-1 overflow-y-auto">
              {!currentSlug ? (
                <div className="p-3">
                  <div className="relative mb-3">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                      type="search"
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      placeholder="Search the knowledge base…"
                      className="w-full pl-7 pr-2 py-1.5 text-[11px] bg-surface-raised border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-blue/40"
                    />
                  </div>

                  {index.length === 0 && (
                    <div className="text-[11px] text-text-muted py-6 text-center">
                      No wiki pages yet. <Link to="/app/wiki" onClick={() => setOpen(false)} className="text-blue-light hover:underline">Open the wiki</Link> to create some.
                    </div>
                  )}

                  {Object.entries(byCategory).sort(([a], [b]) => a.localeCompare(b)).map(([cat, pages]) => (
                    <div key={cat} className="mb-3">
                      <div className="text-[9px] uppercase tracking-wider text-text-muted font-semibold mb-1">{cat}</div>
                      <ul className="space-y-0.5">
                        {pages.map(p => (
                          <li key={p.slug}>
                            <button
                              type="button"
                              onClick={() => setCurrentSlug(p.slug)}
                              className={clsx(
                                'w-full text-left px-2 py-1 rounded text-[11px] text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors flex items-center justify-between group',
                              )}
                            >
                              <span>{p.title}</span>
                              <ArrowRight size={11} className="text-text-muted opacity-0 group-hover:opacity-100" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4">
                  <button
                    type="button"
                    onClick={() => { setCurrentSlug(null); setCurrentPage(null) }}
                    className="text-[10px] text-blue-light hover:underline mb-2 inline-flex items-center gap-1"
                  >
                    ← Back to index
                  </button>
                  {loading ? (
                    <div className="text-[11px] text-text-muted py-4">Loading…</div>
                  ) : currentPage ? (
                    <article className="prose-sm">
                      <div className="text-[9px] uppercase tracking-wider text-text-muted font-semibold">{currentPage.category}</div>
                      <h1 className="text-base font-bold text-text-primary mb-2">{currentPage.title}</h1>
                      {renderContent(currentPage.content)}
                    </article>
                  ) : (
                    <div className="text-[11px] text-rose">Page not found</div>
                  )}
                </div>
              )}
            </div>

            <div className="shrink-0 border-t border-border p-2 text-[9px] text-text-muted text-center">
              TechDashboard · {index.length} wiki pages · <Link to="/app/wiki" onClick={() => setOpen(false)} className="text-blue-light hover:underline">open full wiki</Link>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
